import { Button } from '@/shared/ui/button'
import { t } from '@/shared/lib/i18n'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'

interface Props {
  meta: PaginationMeta
  onPageChange: (page: number) => void
  noun?: string
}

const DataPagination = ({ meta, onPageChange, noun }: Props) => {
  if (meta.totalPages <= 1 && meta.total === 0) return null

  return (
    <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
      <p className="order-2 text-sm text-muted-foreground sm:order-1">
        {t('pagination.page')} {meta.page} {t('pagination.of')} {meta.totalPages}
        {noun && (
          <span className="hidden sm:inline">
            {' '}
            ({meta.total} {noun})
          </span>
        )}
      </p>

      {meta.totalPages > 1 && (
        <div className="order-1 flex items-center gap-1 sm:order-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(meta.page - 1)}
            disabled={meta.page <= 1}
          >
            {t('pagination.previous')}
          </Button>

          <div className="hidden items-center gap-1 sm:flex">
            {buildPageNumbers(meta.page, meta.totalPages).map((item, i) =>
              item === '...' ? (
                <span key={`ellipsis-${i}`} className="px-1 text-sm text-muted-foreground">
                  …
                </span>
              ) : (
                <Button
                  key={item}
                  variant={item === meta.page ? 'default' : 'outline'}
                  size="sm"
                  className="min-w-[2rem] px-2"
                  onClick={() => onPageChange(item as number)}
                >
                  {item}
                </Button>
              ),
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
          >
            {t('pagination.next')}
          </Button>
        </div>
      )}
    </div>
  )
}

function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: (number | '...')[] = [1]

  if (current > 3) pages.push('...')

  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p)
  }

  if (current < total - 2) pages.push('...')

  pages.push(total)
  return pages
}

export default DataPagination
